/**
 * Utility for parsing assistant message content to extract suggested follow-up questions,
 * the transition heading line introducing them, and the main answer content.
 */

export interface ParsedQuestion {
  display: string;
  action: string;
}

export interface ParsedMessageResult {
  mainContent: string;
  headingLine: string | null;
  followUpQuestions: ParsedQuestion[];
}

const numberedListPattern = /^(\d+[.)]|\*|-)\s+(.+)$/;

const suggestionMarkers = [
  'things you might wish to ask',
  'you might want to ask',
  'sormak isteyebileceğin birkaç şey',
  'bana şunları sorabilirsin',
  'ask me about',
  'ask about',
  'here are some',
  'you may also',
  'you might also',
  'perhaps you would',
  'belki de',
  'i could also',
  'ben de sana',
  'would you like to',
  'shall i',
  'şunları da merak',
  "if you'd like",
];

const suggestionKeywords = [
  'interest',
  'explore',
  'ask',
  'question',
  'would you',
  'perhaps',
  'might',
  'wish',
  'share',
  'further',
  'more',
  'aspects',
  'topics',
  'wonder',
  'curious',
  'delve',
  'below',
  'following',
  'merak',
  'sor',
  'ilgini',
  'istersen',
  'şunlar',
  'neler',
  'hakkında',
  'dilersen',
  'ister misin',
  'konular',
  'seek',
  'know',
  'blessed',
  'days',
  'here',
];

const isPipeSuggestionLine = (line: string): boolean => {
  if (!line.includes('|')) return false;
  const parts = line.split('|').map((s) => s.trim());
  if (parts.length !== 2) return false;
  if (parts[0].length < 10 || parts[1].length < 10) return false;
  return true;
};

const extractQuestion = (rawLine: string): ParsedQuestion | null => {
  let text = rawLine;
  const prefixMatch = text.match(numberedListPattern);
  if (prefixMatch) {
    text = prefixMatch[2];
  }
  text = text.trim();
  if (!text) return null;

  if (text.includes('|')) {
    const parts = text.split('|').map((s) => s.trim());
    if (parts.length >= 2 && parts[0].length > 5 && parts[1].length > 5) {
      return { display: parts[0], action: parts[1] };
    }
  }

  if (text.length > 10) {
    return { display: text, action: text };
  }
  return null;
};

export const parseMessageContent = (content: string): ParsedMessageResult => {
  const lines = content.split('\n');
  const followUpQuestions: ParsedQuestion[] = [];
  let suggestionHeadingFound = false;
  let splitIndex = lines.length;

  let inQuestionList = false;

  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i].trim();
    if (!line && !inQuestionList) continue;

    const match = line.match(numberedListPattern);
    if (match) {
      const q = extractQuestion(line);
      if (q) {
        followUpQuestions.unshift(q);
        inQuestionList = true;
        splitIndex = i;
      }
    } else if (inQuestionList && line === '') {
      splitIndex = i;
    } else if (inQuestionList) {
      const lower = line.toLowerCase();
      const endsWithPunctuation =
        /[:?.\-—!]$/.test(line) || line.endsWith('...') || line.endsWith('：');
      const hasMarker = suggestionMarkers.some((marker) => lower.includes(marker));
      const hasKeyword = suggestionKeywords.some((kw) => lower.includes(kw));

      if (
        line.length < 350 &&
        (line.endsWith(':') ||
          line.endsWith('：') ||
          hasMarker ||
          hasKeyword ||
          endsWithPunctuation ||
          i === lines.length - followUpQuestions.length - 1 ||
          (i > 0 && lines[i - 1].trim() === ''))
      ) {
        suggestionHeadingFound = true;
        splitIndex = i;
      }
      break;
    } else {
      if (inQuestionList) break;
    }
  }

  if (followUpQuestions.length === 0) {
    let foundAny = false;
    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i].trim();
      if (!line && !foundAny) continue;

      if (isPipeSuggestionLine(line)) {
        const q = extractQuestion(line);
        if (q) {
          followUpQuestions.unshift(q);
          foundAny = true;
          splitIndex = i;
        }
        continue;
      }

      if (foundAny && line === '') {
        splitIndex = i;
        continue;
      }

      if (foundAny) {
        const lower = line.toLowerCase();
        const endsWithPunctuation =
          /[:?.\-—!]$/.test(line) || line.endsWith('...') || line.endsWith('：');
        const hasMarker = suggestionMarkers.some((marker) => lower.includes(marker));
        const hasKeyword = suggestionKeywords.some((kw) => lower.includes(kw));

        if (
          line.length < 350 &&
          (line.endsWith(':') ||
            line.endsWith('：') ||
            hasMarker ||
            hasKeyword ||
            endsWithPunctuation ||
            i === lines.length - followUpQuestions.length - 1 ||
            (i > 0 && lines[i - 1].trim() === ''))
        ) {
          suggestionHeadingFound = true;
          splitIndex = i;
        }
        break;
      }
      break;
    }
  }

  const mainContentLines = lines.slice(0, splitIndex);
  while (
    mainContentLines.length > 0 &&
    mainContentLines[mainContentLines.length - 1].trim() === ''
  ) {
    mainContentLines.pop();
  }
  const mainContent = mainContentLines.join('\n').trim();
  const headingLine = suggestionHeadingFound ? lines[splitIndex].trim() : null;

  return { mainContent, headingLine, followUpQuestions };
};
